import { searchByPostal } from '@/features/heartrails/searchByPostal';
import {
  buildFormValuesForPublic,
  buildRequestBodyForPublic,
  SearchPublicHomeParamsOutput,
} from '@/helpers/searchPublicHomeHelper';
import { swaggerClientForNonMember } from '@/init/swaggerClient';
import {
  SearchPublicHomeResolver,
  SearchPublicHomeType,
} from '@/schemas/SearchPublicHomeSchema';
import { APP_HOME_LIMIT } from '@/types/types';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useSnackbar } from '@/hooks/useSnackbar';
import {
  api_portalOfficeHospitalListing_responseFilter,
  api_portalOfficeListing_filter,
  api_portalOfficeListingAllType_portalOfficeResponse,
} from '@/init/swagger';

type Props = {
  validatedSearchParams: SearchPublicHomeParamsOutput;
  searchFilters: api_portalOfficeListing_filter[];
  hospitalSearchFilters: api_portalOfficeHospitalListing_responseFilter[];
};

export const useLogic = ({
  validatedSearchParams,
  searchFilters,
  hospitalSearchFilters,
}: Props) => {
  const method = useForm<SearchPublicHomeType>({
    resolver: SearchPublicHomeResolver,
    mode: 'onTouched',
    defaultValues: buildFormValuesForPublic({
      validatedSearchParams,
      searchFilters,
      hospitalSearchFilters,
    }),
  });

  const formValues = method.getValues();
  const { body, page } = buildRequestBodyForPublic({
    validatedSearchParams,
    formValues,
    searchFilters,
    hospitalSearchFilters,
  });

  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const { openSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [paginationInfo, setPaginationInfo] = useState<{
    totalCount: number;
    currentPage: number;
    lastPage: number;
  }>({
    totalCount: 0,
    currentPage: 1,
    lastPage: 1,
  });
  const [targetAddress, setTargetAddress] = useState('');
  const [portalOfficeListings, setPortalOfficeListings] = useState<
    api_portalOfficeListingAllType_portalOfficeResponse[]
  >([]);
  const [latestPortalOfficeListings, setLatestPortalOfficeListings] = useState<
    api_portalOfficeListingAllType_portalOfficeResponse[]
  >([]);

  useEffect(() => {
    setIsLoading(true);
    try {
      const searchListingAllType = async () => {
        const [searchResult, latestPortalOfficeListingsResult] =
          await Promise.all([
            swaggerClientForNonMember.portalOfficeListingAllType
              .postPortalOfficeListingAllTypeSearchEngineForNonMember({
                body,
              })
              .catch((error) => {
                console.error('Error fetching search results:', error);
              }),
            swaggerClientForNonMember.portalOfficeListingAllType
              .postPortalOfficeListingAllTypeSearchEngineForNonMember({
                body: {
                  location: {
                    zipcode: body.location.zipcode,
                    car_minutes: 30,
                  },
                  filters: [],
                  not_in_filters: [],
                  medical_subjects: [],
                  reported_additional_fees: [],
                  offset: 0,
                  limit: 20,
                  sort_by: 'updated_at',
                },
              })
              .catch((error) => {
                console.error('Error fetching search results:', error);
              }),
          ]);

        // NOTE: STと医療機関が詰められたデータ
        setPortalOfficeListings(searchResult?.portal_office_list ?? []);
        setLatestPortalOfficeListings(
          latestPortalOfficeListingsResult?.portal_office_list ?? [],
        );

        if (formValues.zipcode) {
          const location = await searchByPostal({
            postal: formValues.zipcode,
          });
          if (location?.length) {
            const geo = location[0];
            let autoTargetAddressTextByPostCode = '';
            const prefecture = geo.prefecture;
            const city = geo.city;
            const town = geo.town;

            if (prefecture) {
              autoTargetAddressTextByPostCode += prefecture;
            }
            if (city) {
              autoTargetAddressTextByPostCode += city;
            }
            if (town) {
              autoTargetAddressTextByPostCode += town;
            }

            setTargetAddress(autoTargetAddressTextByPostCode);
          } else {
            setTargetAddress('');
            console.error('Error fetching location data');
          }
        }

        setPaginationInfo({
          totalCount: searchResult?.total_count ?? 0,
          currentPage: page,
          lastPage: Math.ceil(
            (searchResult?.total_count ?? 0) / APP_HOME_LIMIT,
          ),
        });
        setIsLoading(false);
      };

      searchListingAllType();
    } catch (_) {
      setIsLoading(false);
      openSnackbar('データの取得に失敗しました', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatedSearchParams]);

  const search = () => {
    const formValues = method.getValues();

    const searchParams = new URLSearchParams();
    searchParams.set('page', String(formValues.page));
    searchParams.set('postCode', formValues.zipcode || '');

    formValues.medical_subjects?.forEach((subject) => {
      searchParams.append('medicalSubjects', String(subject));
    });
    formValues.reported_additional_fees?.forEach((reportedAdditionalFee) => {
      searchParams.append(
        'reportedAdditionalFees',
        String(reportedAdditionalFee),
      );
    });
    Object.entries(formValues.filters).forEach(([key, value]) => {
      if (value) {
        searchParams.append('searchFilters', key);
      }
    });

    startTransition(() => {
      router.push(`/public?${searchParams.toString()}`);
    });
  };

  const onChangePagination = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    method.setValue('page', page);
    search();
  };

  return {
    isPending,
    method,
    search,
    onChangePagination,
    isLoading,
    paginationInfo,
    targetAddress,
    portalOfficeListings,
    latestPortalOfficeListings,
  };
};
